import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ChatOptions } from "../../ui/ChatOptions";
import { displayToast } from "../../app/AppToast";
import { saveAcquisitionChannel } from "../../../domains/user/saveAcquisitionChannel";
import { acquisitionCardDismissedStore } from "../../../domains/user/stores/acquisitionCardStore";
import {
  ACQUISITION_CHANNELS,
  acquisitionChannelLabel,
  type AcquisitionChannelValue,
} from "../../../domains/user/utils/acquisitionChannels";

/**
 * "How did you hear about us?" in the phone's composer band, after the first
 * finished answer: the phone's signup no longer asks it. The desktop asks it on
 * the home (`DesktopAcquisitionCard`); both write through
 * `saveAcquisitionChannel` and hide through the same session store.
 *
 * The cross is "Later". The card goes away once the reloaded user carries the
 * answer, which is `useComposerBand`'s condition for rendering this.
 */
export function AcquisitionSuggestionCard(): React.JSX.Element {
  const { t } = useTranslation();
  // The pick, and the save in flight, through RHF (no useState here).
  const { watch, setValue, handleSubmit, formState } = useForm<{
    channel: AcquisitionChannelValue | null;
  }>({ defaultValues: { channel: null } });
  const selected = watch("channel");

  const submit = handleSubmit(async ({ channel }) => {
    if (channel === null)
      throw new Error("should not happen: Continue without a channel");
    if ((await saveAcquisitionChannel(channel)) === "failed")
      displayToast(t("Something went wrong. Please try again."), "error");
  });

  // `pb-3`: the band stack has no gap, so the card above the input owns it.
  return (
    <div className="pb-3">
      <ChatOptions
        title={t("How did you hear about us?")}
        select="single"
        options={ACQUISITION_CHANNELS.map((channel) => ({
          id: channel,
          label: acquisitionChannelLabel(t, channel),
        }))}
        selectedIds={selected === null ? [] : [selected]}
        // The saved answer is the one picked when Continue was pressed.
        onSelect={
          formState.isSubmitting
            ? null
            : (id) => {
                const channel = ACQUISITION_CHANNELS.find(
                  (value) => value === id
                );
                if (channel === undefined)
                  throw new Error(`should not happen: channel ${id} missing`);
                setValue("channel", channel);
              }
        }
        freeText={null}
        onContinue={() => {
          void submit();
        }}
        continueLabel={t("Continue")}
        continueDisabled={selected === null || formState.isSubmitting}
        onClose={() => {
          acquisitionCardDismissedStore.set(true);
        }}
        pager={null}
        placement="band"
      />
    </div>
  );
}
